import { useEffect, useRef, useState } from 'react';
import { keyOutWhite } from '../../utils/chromaKey';
import { effectiveCell, resolveSheetRow, type SpriteSheetCfg } from '../../utils/spriteFrame';

// ─────────────────────────────────────────────────────────────────────────────
// Meio corpo de um personagem/monstro a partir da spritesheet do jogo: pega o
// quadro "parado de frente" (direção S, coluna 0), acha o contorno visível e
// recorta a metade de cima, ampliada em pixel art (sem suavização).
// Sem sprite (ou falha ao carregar) → não desenha nada (onFail).
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_CFG: SpriteSheetCfg = { cols: 3, rows: 8, scale: 1, offsetX: 0, offsetY: 0 };
/** Fração de cima do contorno que entra no retrato (cabeça + tronco). */
const BUST = 0.58;

interface Props {
  sprite: string;
  cfg?: SpriteSheetCfg | null;
  /** Altura desenhada (px CSS). */
  height: number;
  className?: string;
  onFail?: () => void;
}

export default function SpritePortrait({ sprite, cfg, height, className, onFail }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive || !ref.current) return;
      const sheet = keyOutWhite(img);
      const c = cfg ?? DEFAULT_CFG;
      const cols = Math.max(1, c.cols), rows = Math.max(1, c.rows);
      const row = resolveSheetRow(1 /* S — de frente para a câmera */, { ...c, cols, rows });
      const cell = effectiveCell({ ...c, cols, rows }, row, 0);
      const fw = Math.floor(sheet.width / cols), fh = Math.floor(sheet.height / rows);
      const sx = Math.max(0, Math.min(sheet.width - fw, Math.round(cell.srcOffsetX)));
      const sy = Math.max(0, Math.min(sheet.height - fh, Math.round(row * fh + cell.srcOffsetY)));

      // Contorno do personagem: o MAIOR bloco contínuo de linhas com conteúdo.
      // Ignora respingos soltos e sobras da linha vizinha da folha (pés de
      // quem está em cima invadindo o quadro).
      const px = sheet.getContext('2d')!.getImageData(sx, sy, fw, fh).data;
      const solid = (x: number, y: number) => px[(y * fw + x) * 4 + 3] > 40;
      const minCount = Math.max(2, Math.round(fw * 0.04));
      let bestStart = -1, bestLen = 0, runStart = -1;
      for (let y = 0; y <= fh; y++) {
        let n = 0;
        if (y < fh) for (let x = 0; x < fw; x++) if (solid(x, y)) n++;
        if (y < fh && n >= minCount) {
          if (runStart < 0) runStart = y;
        } else if (runStart >= 0) {
          if (y - runStart > bestLen) { bestLen = y - runStart; bestStart = runStart; }
          runStart = -1;
        }
      }
      if (bestLen <= 0) { onFail?.(); return; }
      const minY = bestStart, maxY = bestStart + bestLen - 1;
      let minX = fw, maxX = -1;
      for (let x = 0; x < fw; x++) {
        let n = 0;
        for (let y = minY; y <= maxY; y++) if (solid(x, y)) n++;
        if (n >= 2) { if (x < minX) minX = x; if (x > maxX) maxX = x; }
      }
      if (maxX < 0) { onFail?.(); return; }
      const bw = maxX - minX + 1;
      const bh = Math.max(1, Math.round((maxY - minY + 1) * BUST));

      const canvas = ref.current;
      const dpr = window.devicePixelRatio || 1;
      const outH = Math.round(height * dpr);
      const outW = Math.round(outH * (bw / bh));
      canvas.width = outW;
      canvas.height = outH;
      canvas.style.height = `${height}px`;
      canvas.style.width = `${Math.round(height * (bw / bh))}px`;
      const ctx = canvas.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(sheet, sx + minX, sy + minY, bw, bh, 0, 0, outW, outH);
      setReady(true);
    };
    img.onerror = () => onFail?.();
    img.src = `/sprites/${sprite}.png`;
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprite, height]);

  return <canvas ref={ref} className={className} style={{ imageRendering: 'pixelated', opacity: ready ? 1 : 0 }} aria-hidden="true" />;
}
